import axios from 'axios';

// Mesma origem em produção (Render); override opcional para dev remoto
const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de resposta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const apiError = String(error.response?.data?.error || '');
      const isDashboardAuth = /token|fornecido|expirado|inválido|mal formatado/i.test(apiError);
      if (isDashboardAuth) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API de pedidos
export const pedidosApi = {
  // Listar pedidos com filtros
  listar: async (params = {}) => {
    const response = await api.get('/api/pedidos', { params });
    return response.data;
  },

  // Exportar listagem filtrada (Excel)
  exportarExcel: async (params = {}) => {
    const response = await api.get('/api/pedidos/export', {
      params,
      responseType: 'blob',
      timeout: 180000
    });
    return response;
  },

  // Buscar pedido por ID
  buscarPorId: async (id) => {
    const response = await api.get(`/api/pedidos/${id}`);
    return response.data;
  },

  // Atualizar observação
  atualizarObservacao: async (id, observacao) => {
    const response = await api.put(`/api/pedidos/${id}/observacao`, { observacao });
    return response.data;
  },

  // Buscar tracking (force=true consulta transportadora ignorando cache)
  buscarTracking: async (id, force = false) => {
    const params = force ? { force: 'true' } : {};
    const response = await api.get(`/api/pedidos/${id}/tracking`, { params });
    return response.data;
  },

  // Obter plataformas
  plataformas: async () => {
    const response = await api.get('/api/pedidos/plataformas');
    return response.data;
  },

  // Obter transportadoras
  transportadoras: async () => {
    const response = await api.get('/api/pedidos/transportadoras');
    return response.data;
  },

  // Forçar integração Lexos → Tracking (reprocessar pedido ausente)
  forcarIntegracao: async (codigo, { force = false } = {}) => {
    const response = await api.post(
      '/api/pedidos/forcar-integracao',
      { codigo, force: Boolean(force), reindex: Boolean(force) },
      { timeout: 180000 }
    );
    return response.data;
  },

  // Reindexar: reconsulta Lexos/XML/rastreio mesmo se já indexado
  reindexar: async (codigo) => {
    const response = await api.post(
      '/api/pedidos/forcar-integracao',
      { codigo, force: true, reindex: true, acao: 'reindexar' },
      { timeout: 180000 }
    );
    return response.data;
  },

  forcarIntegracaoLotePreview: async (dias = 30) => {
    const response = await api.get('/api/pedidos/forcar-integracao-lote/preview', {
      params: { dias },
      timeout: 60000
    });
    return response.data;
  },

  forcarIntegracaoLote: async ({ dias = 30, limit = 200 } = {}) => {
    const response = await api.post('/api/pedidos/forcar-integracao-lote', { dias, limit }, { timeout: 600000 });
    return response.data;
  },

  // Diagnóstico de pedido ausente (requer login; consulta Lexos pode demorar)
  diagnosticar: async (codigo) => {
    const response = await api.get('/api/pedidos/diagnostico-integracao', { params: { codigo }, timeout: 120000 });
    return response.data;
  },

  // Consulta em massa por códigos de pedido
  consultarLote: async (codigos) => {
    const response = await api.post('/api/pedidos/consulta-lote', { codigos }, { timeout: 120000 });
    return response.data;
  },

  // Rastreio público (pode disparar auto-integração Lexos se o pedido ainda não existir)
  rastreioPublico: async (numero) => {
    const response = await api.get(`/api/pedidos/rastreio/${encodeURIComponent(numero)}`, {
      timeout: 180000
    });
    return response.data;
  }
};

// API de autenticação
export const authApi = {
  login: async (email, senha) => {
    const response = await api.post('/api/auth/login', { email, senha });
    return response.data;
  },

  register: async (nome, email, senha) => {
    const response = await api.post('/api/auth/register', { nome, email, senha });
    return response.data;
  },

  perfil: async () => {
    const response = await api.get('/api/auth/profile');
    return response.data;
  },

  atualizarSenha: async (senhaAtual, novaSenha) => {
    const response = await api.put('/api/auth/password', { senhaAtual, novaSenha });
    return response.data;
  },

  listarOperadores: async () => {
    const response = await api.get('/api/auth/operadores');
    return response.data;
  },

  definirOperadorAtivo: async (id, ativo) => {
    const response = await api.patch(`/api/auth/operadores/${id}`, { ativo });
    return response.data;
  }
};

export default api;
